import { useEffect } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";

/**
 * When the agent writes a plan, open the session context panel to the Plan view
 * so the user sees the freshly-laid-out task plan on the right without hunting
 * for it under Apps.
 *
 * Mirrors `useInitializationPanelSync`: navigation only, driven by the AG-UI
 * tool-call stream, no poll of its own. `write_plan` is the plan's create /
 * whole-replace call, so opening on it surfaces the plan exactly when the agent
 * lays it out (or re-lays it). The incremental plan tools (`update_task_status`,
 * `add_task`, `read_plan`, …) deliberately do not re-open the panel, so a user
 * who navigated elsewhere mid-run is not yanked back on every task update.
 */
export function usePlanPanelSync(agent: AbstractAgent) {
  const { openPanel } = useSessionContextPanel();
  useEffect(() => {
    const sub = agent.subscribe({
      onToolCallEndEvent: ({ toolCallName }) => {
        if (toolCallName !== "write_plan") return;
        openPanel(SessionContextPanelType.PLAN);
      },
    });
    return () => sub.unsubscribe();
  }, [agent, openPanel]);
}
